'use client'

import React, { useEffect, useState, useRef } from 'react'
import { useParams, usePathname, useRouter } from 'next/navigation'
import Image from 'next/image'
import { HiArrowRight } from 'react-icons/hi'


import { scrollToElementWithOffset } from '@/src/helpers'
import BurgerButton from '../burger-button'
import PrimaryButton from '@/src/components/ui/primary-button'
import { getIcon } from '@/src/helpers/iconMapper'
import HeaderLogo from '@/public/logo.png'
import siteData from '@/public/content/site-data.json'

const presentationByProduct: Record<
  string,
  {
    color: string
    accentColor: string
  }
> = {
  tradevision: {
    color: '#7c3aed',
    accentColor: '#38bdf8',
  },
  profiles: {
    color: '#06b6d4',
    accentColor: '#f59e0b',
  },
  'casino-ai': {
    color: '#e85d97',
    accentColor: '#7ea2ff',
  }
}

const Header = () => {
  const pathname = usePathname()
  const router = useRouter()
  const params = useParams()
  
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [headerHeight, setHeaderHeight] = useState<number>(88)
  const headerRef = useRef<HTMLDivElement>(null)
  const mobileNavId = 'mobile-navigation'

  const isHomePage = pathname === '/'
  const documentExists = typeof document !== 'undefined' && typeof window !== 'undefined'

  const headerBackground = 'rgba(8, 13, 24, 0.96)'

  // Get navigation data from site-data.json
  const navLinks = siteData.navigation.links.map(link => {
    const sectionId = link.href.replace('#', '')
    return {
      label: link.label,
      onClick: () => handleScrollToSection(sectionId)
    }
  })
  const ctaButtonText = siteData.navigation.ctaButtonText || 'Book a demo'
  const ctaSubject = siteData.navigation.ctaSubject || 'Book a Demo'
  const ctaHref = (siteData.navigation as { ctaHref?: string }).ctaHref
  const salesEmail =
    siteData.company.contact.salesEmail ||
    siteData.company.contact.primaryEmail ||
    'sales@gambly.ai'

  const products = siteData.navigation.productMenuItems.map(product => ({
    icon: product.icon,
    name: product.label,
    subtitle: product.description,
    id: product.id,
    href: product.route
  }))

  const getElementById = (id: string) => {
    return documentExists ? document.getElementById(id) : null
  }

  const handleScrollToSection = (sectionId: string) => {
    setIsOpen(false)

    if (!isHomePage) {
      router.push(`/#${sectionId}`)
      return
    }
    
    const element = getElementById(sectionId)
    if (element) {
      scrollToElementWithOffset(element)
    }
  }

  const handleProductClick = (route: string) => {
    router.push(route)
    setIsOpen(false)
  }

  useEffect(() => {
    if (!documentExists) return
    
    const hash = location.hash
    if (!hash) return

    const sectionId = hash.substring(1)
    const sectionIdMap: Record<string, string> = {
      'solutions': 'products',
      'results': 'products',
      'cases': 'products',
      'impact': 'products'
    }
    
    const targetId = sectionIdMap[sectionId] || sectionId
    const element = document.getElementById(targetId)
    
    if (element) {
      setTimeout(() => {
        scrollToElementWithOffset(element)
      }, 100)
    }
  }, [pathname, params, documentExists])

  // Disable body scroll when mobile nav is open
  useEffect(() => {
    if (!documentExists) return

    if (isOpen) {
      // Store the original overflow value
      const originalOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      
      return () => {
        document.body.style.overflow = originalOverflow || ''
      }
    }
  }, [isOpen, documentExists])

  useEffect(() => {
    if (!isOpen || !documentExists) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, documentExists])

  // Calculate header height for sidebar positioning
  useEffect(() => {
    const updateHeaderHeight = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight
        setHeaderHeight(height)
      }
    }
    
    updateHeaderHeight()
    window.addEventListener('resize', updateHeaderHeight)
    
    return () => {
      window.removeEventListener('resize', updateHeaderHeight)
    }
  }, [])

  return (
    <>
      {/* Backdrop overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[99] bg-black/50 lg:hidden transition-opacity duration-200"
          onClick={() => setIsOpen(false)}
          aria-hidden={!isOpen}
        />
      )}
      {/* Mobile navigation sidebar */}
      <nav
        id={mobileNavId}
        className={`fixed right-0 bottom-0 z-[100] w-80 bg-bg-dark border-l border-border-subtle px-6 py-8 flex flex-col gap-8 lg:hidden transition-transform duration-300 ease-out overflow-y-auto ${
          isOpen ? 'translate-x-0 opacity-100 pointer-events-auto' : 'translate-x-full opacity-0 pointer-events-none'
        }`}
        style={{
          top: `${headerHeight}px`
        }}
        aria-hidden={!isOpen}
        aria-label="Mobile navigation"
      >
        <div className="flex-1 min-h-0 space-y-8">
          <div>
            <div className="space-y-2">
              {products.map((product, index) => {
                const IconComponent = getIcon(product.icon)
                const palette = presentationByProduct[product.id] ?? { color: '#4c51bf', accentColor: '#a5b4fc' }
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleProductClick(product.href)}
                    className="w-full flex items-center gap-4 p-4 rounded-xl bg-white/[0.04] border border-white/5 hover:border-link-blue/40 transition-colors text-left"
                  >
                    {IconComponent && (
                      <div
                        className="card-surface__icon flex items-center justify-center flex-shrink-0"
                        style={{
                          borderColor: `${palette.color}44`,
                          boxShadow: `0 15px 30px ${palette.color}33, inset 0 1px 0 rgba(255,255,255,0.12)`
                        }}
                      >
                        <IconComponent className="w-7 h-7 text-link-blue" />
                      </div>
                    )}
                    <span className="flex-1 min-w-0">
                      <span className="mb-1 block text-label uppercase tracking-wide text-link-blue/80">
                        {product.subtitle}
                      </span>
                      <span className="block text-lg font-bold text-white leading-tight">
                        {product.name}
                      </span>
                    </span>
                  </button>
                )
              })}
              {navLinks?.map((link, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => {
                    link.onClick()
                    setIsOpen(false)
                  }}
                  className="w-full text-left text-link-blue hover:text-primary transition-colors text-lg py-2"
                >
                  {link.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>
      <header
        className="sticky top-0 z-[101] border-b border-white/[0.08] backdrop-blur-header"
        style={{ background: headerBackground }}
      >
        <div className="w-full">
          <div ref={headerRef} className="max-w-container mx-auto px-4 py-4">
            <div className="w-full flex flex-row justify-between items-center">
              <a className="flex items-center" href={isHomePage ? '#' : '/'}>
                <Image
                  src={HeaderLogo}
                  onClick={() => (isHomePage ? handleScrollToSection('home') : router.push('/'))}
                  alt="Gambly.AI"
                  height={72}
                  width={72}
                  className="h-14 w-auto cursor-pointer sm:h-16"
                  style={{
                    width: 'auto'
                  }}
                />
              </a>
              <nav className="hidden items-center gap-9 text-base lg:flex" aria-label="Primary">
                {products.map((product, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleProductClick(product.href)}
                    className="text-link-blue cursor-pointer hover:text-primary transition-colors"
                  >
                    {product.name.replace('TradeVision ', '')}
                  </button>
                ))}
                {navLinks?.map((link, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={link.onClick}
                    className="text-link-blue cursor-pointer hover:text-primary transition-colors"
                  >
                    {link.label}
                  </button>
                ))}
              </nav>
              <div className="flex flex-row items-center gap-4">
                <PrimaryButton
                  href={ctaHref || `mailto:${salesEmail}?subject=${encodeURIComponent(ctaSubject)}`}
                  size="md"
                >
                  <span className="hidden lg:inline">{ctaButtonText}</span>
                  <span className="lg:hidden">Pilot</span>
                </PrimaryButton>
                <BurgerButton isOpen={isOpen} setIsOpen={setIsOpen} controlsId={mobileNavId} />
              </div>
            </div>
          </div>
        </div>
      </header>
    </>
  )
}

export default Header
